import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Mic, Send, X, User, Mail, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './Button';
import { generateAIResponse } from '../../../../services/ai/landing';
import { leadService } from '../../../../services/leads';
import { projectService } from '../../../../services/database/projects';

const INITIAL_MESSAGE = "Olá! Sou Lega, especialista em transformar ideias em inovações tecnológicas! 🚀 Me conta, que tipo de projeto ou ideia você gostaria de explorar hoje? ✨";

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
}

interface ChatInterfaceProps {
  onCaptureEmail: (email: string, phone: string, name: string) => void;
}

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

export function ChatInterface({ onCaptureEmail }: ChatInterfaceProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    { role: 'assistant', content: INITIAL_MESSAGE }
  ]);
  const [isRecording, setIsRecording] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messageCount, setMessageCount] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactData, setContactData] = useState<ContactFormData>({
    name: '',
    email: '',
    phone: ''
  });
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsMinimized(false);
    }, 15000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!isMinimized && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isMinimized]);

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        toast.info('Funcionalidade de voz em desenvolvimento!');
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Não foi possível acessar o microfone');
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const newMessage: Message = { role: 'user', content: input };
    setMessages(prev => [...prev, newMessage]);
    setInput('');
    setIsLoading(true);
    setIsExpanded(true);

    try {
      const aiResponse = await generateAIResponse(
        [...messages, newMessage],
        messageCount
      );
      
      setMessages(prev => [...prev, { role: 'assistant', content: aiResponse }]);
      setMessageCount(prev => prev + 1);
      
      // Show contact form after the third message
      if (messageCount === 2) {
        setShowContactForm(true);
        setMessages(prev => [...prev, { 
          role: 'assistant', 
          content: "Adorei explorar suas ideias! Para continuarmos desenvolvendo esse projeto incrível, que tal me contar um pouco mais sobre você? 😊" 
        }]);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Ops! Tive um probleminha para processar sua mensagem');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmitContact = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!contactData.email || !contactData.phone || !contactData.name) {
      toast.error('Por favor, preencha todos os campos');
      return;
    }

    try {
      // Save lead to Supabase
      const lead = await leadService.saveLead({
        name: contactData.name,
        email: contactData.email,
        phone: contactData.phone
      });

      // Create project with chat conversation
      await projectService.createProjectFromChat(
        'Novo Projeto', // Título padrão
        '', // Ideia de negócio será preenchida depois
        messages,
        {
          name: contactData.name,
          email: contactData.email,
          phone: contactData.phone,
          id: lead.id // Passar o ID do lead que acabamos de criar
        }
      );

      // Notify parent component
      onCaptureEmail(contactData.email, contactData.phone, contactData.name);
      
      // Hide form and show thank you message
      setShowContactForm(false);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: `Que ótimo ${contactData.name}! 🎉 Adorei conhecer você e suas ideias incríveis. Nossa equipe vai analisar seu projeto com todo carinho e em breve entraremos em contato para darmos o próximo passo nessa jornada de inovação! ✨`
        }
      ]);
    } catch (error) {
      console.error('Error saving lead:', error);
      toast.error('Erro ao salvar suas informações. Por favor, tente novamente.');
    }
  };

  const messageVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  const handleInputFocus = () => {
    if (isMinimized) {
      setIsMinimized(false);
    }
  };

  return (
    <div className="relative max-w-2xl mx-auto">
      {/* Chat Interface */}
      <AnimatePresence>
        {isMinimized ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="relative w-full"
          >
            <div className="bg-[#0A0A0A]/80 backdrop-blur-md rounded-xl border border-white/[0.12] cursor-pointer overflow-hidden">
              <div className="flex items-center p-3 md:p-4">
                <input
                  type="text"
                  placeholder="digite sua ideia, e deixe a LegatIA te ajudar..."
                  className="flex-1 bg-transparent text-white/60 text-sm md:text-base outline-none"
                  onFocus={handleInputFocus}
                  ref={inputRef}
                />
                <div className="flex gap-2">
                  <button className="p-2 rounded-lg hover:bg-white/[0.08] transition-colors">
                    <Mic className="w-4 h-4 md:w-5 md:h-5 text-white/60" />
                  </button>
                  <button className="p-2 rounded-lg hover:bg-white/[0.08] transition-colors">
                    <Send className="w-4 h-4 md:w-5 md:h-5 text-white/60" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            className="relative"
          >
            <div className="bg-[#0A0A0A]/80 backdrop-blur-md rounded-xl border border-white/[0.12] overflow-hidden">
              {/* Header */}
              <div className="p-4 border-b border-white/[0.08] flex items-center justify-between">
                <h3 className="text-white font-medium">Fale com LegatIA</h3>
                <button
                  onClick={() => setIsMinimized(true)}
                  className="p-1 hover:bg-white/[0.08] rounded-lg transition-colors"
                >
                  <X className="w-4 h-4 text-white/60" />
                </button>
              </div>

              {/* Messages */}
              <div
                ref={chatContainerRef}
                className="p-4 h-[300px] md:h-[400px] lg:h-[500px] overflow-y-auto space-y-4 scrollbar-thin scrollbar-thumb-white/10 scrollbar-track-transparent"
              >
                <AnimatePresence>
                  {messages.map((message, index) => (
                    <motion.div
                      key={index}
                      variants={messageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-xl ${
                          message.role === 'user'
                            ? 'bg-[#006FFE] text-white'
                            : 'bg-white/[0.08] text-white'
                        }`}
                      >
                        {message.role === 'assistant' ? (
                          <div className="prose prose-invert prose-sm max-w-none">
                            {message.content.split('\n').map((line, i) => {
                              if (line.startsWith('•')) {
                                return (
                                  <div key={i} className="flex items-start space-x-2 mt-1">
                                    <span className="text-blue-400">•</span>
                                    <span>{line.slice(1).trim()}</span>
                                  </div>
                                );
                              } else if (line.startsWith('🎯') || line.startsWith('🔄') || line.startsWith('🎨')) {
                                return (
                                  <div key={i} className="font-medium text-blue-400 mt-2 mb-1">
                                    {line}
                                  </div>
                                );
                              } else if (line.includes('**')) {
                                return (
                                  <div key={i} className="mt-1">
                                    {line.split('**').map((part, j) => (
                                      j % 2 === 0 ? 
                                        <span key={j}>{part}</span> : 
                                        <span key={j} className="font-medium text-blue-400">{part}</span>
                                    ))}
                                  </div>
                                );
                              } else if (line.startsWith('Imagina só:')) {
                                return (
                                  <div key={i} className="font-medium text-lg text-blue-400 mt-2 mb-1">
                                    {line}
                                  </div>
                                );
                              } else {
                                return <div key={i} className="mt-1">{line}</div>;
                              }
                            })}
                          </div>
                        ) : (
                          message.content
                        )}
                      </div>
                    </motion.div>
                  ))}
                  {isLoading && (
                    <motion.div
                      variants={messageVariants}
                      initial="initial"
                      animate="animate"
                      exit="exit"
                      className="flex justify-start"
                    >
                      <div className="bg-white/[0.08] p-3 rounded-xl">
                        <div className="flex space-x-2">
                          <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce" />
                          <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce delay-100" />
                          <div className="w-2 h-2 bg-white/40 rounded-full animate-bounce delay-200" />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Contact Form */}
              <AnimatePresence>
                {showContactForm && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 20 }}
                    className="p-4 border-t border-white/[0.08]"
                  >
                    <form onSubmit={handleSubmitContact} className="space-y-4">
                      <div className="space-y-2">
                        <label className="block text-sm text-white/60">Nome</label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="text"
                            value={contactData.name}
                            onChange={(e) => setContactData({ ...contactData, name: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 bg-white/[0.08] rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#006FFE]/50"
                            placeholder="Seu nome"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm text-white/60">E-mail</label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="email"
                            value={contactData.email}
                            onChange={(e) => setContactData({ ...contactData, email: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 bg-white/[0.08] rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#006FFE]/50"
                            placeholder="seu@email.com"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <label className="block text-sm text-white/60">Telefone</label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/40" />
                          <input
                            type="tel"
                            value={contactData.phone}
                            onChange={(e) => setContactData({ ...contactData, phone: e.target.value })}
                            className="w-full pl-10 pr-4 py-2 bg-white/[0.08] rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#006FFE]/50"
                            placeholder="(00) 00000-0000"
                          />
                        </div>
                      </div>
                      <Button type="submit" className="w-full">
                        Enviar
                      </Button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Input */}
              <div className="p-4 border-t border-white/[0.08]">
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    placeholder="Digite sua mensagem..."
                    className="flex-1 px-4 py-2 bg-white/[0.08] rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#006FFE]/50"
                  />
                  <button
                    onClick={isRecording ? handleStopRecording : handleStartRecording}
                    className={`p-2 rounded-lg transition-colors ${
                      isRecording ? 'bg-red-500/20 text-red-500' : 'hover:bg-white/[0.08] text-white/60'
                    }`}
                  >
                    <Mic className="w-5 h-5" />
                  </button>
                  <button
                    onClick={handleSendMessage}
                    disabled={!input.trim()}
                    className="p-2 hover:bg-white/[0.08] rounded-lg transition-colors text-white/60 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Send className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
